import { useRef, useState, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
  useInView,
  animate,
} from "framer-motion";
import { MessageCircle, Phone, ChevronDown, Zap, Clock3, ShieldCheck } from "lucide-react";
import { WA_DEFAULT, PHONE_DISPLAY } from "./shared";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const CountUp = ({ to, decimals = 0, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: "easeOut",
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref}>
      {val.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
      {suffix}
    </span>
  );
};

const STATS = [
  { value: 1400, suffix: "+", label: "Systems Revived" },
  { value: 99.2, suffix: "%", decimals: 1, label: "Success Rate" },
  { value: 24, suffix: "h", label: "Avg Turnaround" },
  { value: 4.9, suffix: "★", decimals: 1, label: "Google Rating" },
];

const CHIPS = [
  { icon: MessageCircle, text: "Direct WhatsApp support" },
  { icon: ShieldCheck, text: "Clear diagnosis & repair options" },
  { icon: Zap, text: "Home & small-business IT help" },
];

export const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const emblemY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });

  const onMouseMove = (e) => {
    const r = sectionRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left - r.width / 2) / 40);
    my.set((e.clientY - r.top - r.height / 2) / 40);
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col overflow-hidden bg-void bg-grid-tech pt-[72px]"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[34rem] h-[34rem] rounded-full bg-brand-lime/10 blur-[140px]" />
        <div className="absolute top-1/3 -right-40 w-[38rem] h-[38rem] rounded-full bg-brand-cyan/10 blur-[160px]" />
        <div className="absolute bottom-0 left-1/3 w-[26rem] h-[26rem] rounded-full bg-blue-600/10 blur-[140px]" />
        <motion.div
          className="absolute top-1/4 left-[-20%] w-[140%] h-px bg-gradient-to-r from-transparent via-brand-lime/40 to-transparent"
          animate={{ opacity: [0.2, 0.7, 0.2] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-2/3 left-[-20%] w-[140%] h-px bg-gradient-to-r from-transparent via-brand-cyan/40 to-transparent"
          animate={{ opacity: [0.6, 0.2, 0.6] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      <div className="relative z-10 flex-1 flex items-center w-full">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 sm:py-20 grid lg:grid-cols-2 gap-16 items-center w-full">
        <div>
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="inline-flex items-center gap-2 border border-brand-lime/40 bg-brand-lime/10 rounded-full px-4 py-1.5 mb-8"
            data-testid="hero-status-pill"
          >
            <span className="w-2 h-2 rounded-full bg-brand-lime animate-pulse" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-lime">
              Kembangan Lab Open · Quick Diagnostics · 90-Day Warranty
            </span>
          </motion.div>

          <h1 className="font-display font-black uppercase leading-[0.92] tracking-tight text-5xl sm:text-6xl lg:text-7xl xl:text-[5.2rem] text-white">
            <MaskedLine delay={0.25}>Computer, Laptop</MaskedLine>
            <MaskedLine delay={0.38}>
              <span className="text-brand-lime text-glow-lime italic">&amp; Mac Repair</span>
            </MaskedLine>
            <MaskedLine delay={0.51}>in Singapore.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="mt-7 text-base sm:text-lg text-slate-400 max-w-xl leading-relaxed"
          >
            AFZ Tech Solution helps with hardware faults, slow systems, startup
            problems, Windows and macOS setup, and everyday IT support. Tell us
            what is happening — we'll work out the next step.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <a
              data-testid="hero-whatsapp-cta-button"
              href={WA_DEFAULT}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 bg-brand-lime text-void font-display font-bold uppercase tracking-wide text-lg px-8 py-4 rounded-full transition-all duration-300 hover:bg-brand-limedark hover:scale-[1.03] shadow-[0_0_30px_rgba(170,255,0,0.4)]"
            >
              <MessageCircle className="w-5 h-5 transition-transform duration-300 group-hover:-rotate-12" />
              Request Support
            </a>
            <a
              data-testid="hero-call-cta-button"
              href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`}
              className="inline-flex items-center gap-3 border border-brand-cyan/50 text-brand-cyan font-display font-bold uppercase tracking-wide text-lg px-8 py-4 rounded-full transition-all duration-300 hover:bg-brand-cyan/10 hover:border-brand-cyan"
            >
              <Phone className="w-5 h-5" />
              Call {PHONE_DISPLAY}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
          >
            {CHIPS.map((c) => (
              <div key={c.text} className="flex items-center gap-2.5">
                <c.icon className="w-4 h-4 text-brand-lime" />
                <span className="text-sm text-slate-300">{c.text}</span>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: emblemY }} className="relative hidden lg:flex justify-center">
          <motion.div style={{ x: sx, y: sy }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.7, rotate: -12 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1.3, delay: 0.4, ease: EASE }}
              className="relative w-[26rem] h-[26rem]"
              data-testid="hero-logo-emblem"
            >
              <div className="absolute inset-0 rounded-full bg-brand-lime/15 blur-[70px]" />
              <div className="absolute -inset-5 rounded-full border border-dashed border-brand-lime/30 animate-spin-slow" />
              <div className="absolute -inset-12 rounded-full border border-brand-cyan/15" />
              <img
                src="/assets/logo.png"
                alt="AFZ Tech Solutions official logo"
                className="relative w-full h-full object-contain animate-floaty drop-shadow-[0_0_35px_rgba(170,255,0,0.5)]"
              />
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.3, duration: 0.8 }}
                className="absolute -right-8 top-8 bg-navy/90 backdrop-blur border border-brand-lime/40 rounded-xl px-4 py-3 shadow-[0_0_24px_rgba(170,255,0,0.2)]"
              >
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-brand-lime" />
                  <span className="font-display font-bold text-white uppercase text-sm">SSD · 10× Faster Boot</span>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.5, duration: 0.8 }}
                className="absolute -left-10 bottom-12 bg-navy/90 backdrop-blur border border-brand-cyan/40 rounded-xl px-4 py-3 shadow-[0_0_24px_rgba(34,211,238,0.2)]"
              >
                <div className="flex items-center gap-2">
                  <Clock3 className="w-4 h-4 text-brand-cyan" />
                  <span className="font-display font-bold text-white uppercase text-sm">24h Express Turnaround</span>
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
      </div>

      <div className="relative z-10 border-t border-white/10 bg-void/70 backdrop-blur">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map((s) => (
            <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="font-display text-3xl font-extrabold text-brand-lime">
                <CountUp to={s.value} suffix={s.suffix} decimals={s.decimals || 0} />
              </p>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-1">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-24 left-1/2 -translate-x-1/2 text-slate-500 hidden md:block"
      >
        <ChevronDown className="w-6 h-6" />
      </motion.div>
    </section>
  );
};
